import { h, type JSX } from 'preact'; // JSX
import { useState } from 'preact/hooks';
import type { PromoMetadata, PromoState } from './usePromoCode';
import { sanitizePromoHtml } from '../utils/sanitizeHtml';

/**
 * Rabattcode-Badge for the PDP.
 *
 * Visuals live in scss/orthomol/components/eshop/_promo-banner.scss
 * (BEM root `.promo-banner`, see Figma node 4832:10625).
 *
 * Each state renders a two-part layout: a 16px headline that names the
 * concrete benefit + code, and a 14px subline that explains when/where
 * the discount actually applies. The wording keeps Newsletter copy honest:
 * we only quote a hard discount when Shopware's scope is `cart` or
 * `delivery` — for everything else the headline stays generic.
 *
 * TODO(orthomol-onlinekomm): review wording with Head of Online Communications.
 * The strings in COPY are the single source of truth; the comms team can
 * iterate on tone without touching the JSX.
 */

export const COPY = {
  loading: 'Rabattcode wird geprüft …',

  // Applied headline — composed in JSX as:
  //   {appliedHeadlinePrefix}<strong>{code}</strong>{appliedHeadlineConnector}<strong className="…discount">{appliedDiscount*}</strong>
  // Only the trailing discount span is tinted bordeaux; the code is bold but
  // sits in regular dark text.
  appliedHeadlinePrefix: 'Dein Code ',
  appliedHeadlineConnector: ' sichert dir ',
  appliedDiscountCartPercentage: (amount: string) => `${amount}\u00A0Rabatt`,
  appliedDiscountCartFixed: (amount: string) => `${amount} Rabatt`,
  appliedDiscountCartGeneric: 'einen Rabatt',
  appliedDiscountShippingFree: 'versandkostenfreie Lieferung',
  appliedDiscountShippingPartial: (amount: string) => `${amount} auf den Versand`,
  appliedDiscountShippingGeneric: 'einen Versandrabatt',
  appliedDiscountGeneric: 'einen Rabatt',

  // Sublines
  appliedSublineDefault: 'Rabatt wird automatisch im Warenkorb abgezogen.',
  // priceOnPdp:true — the discount is already shown as a Streichpreis on the PDP, so
  // "im Warenkorb" would be misleading (ORT-3110).
  appliedSublinePriceOnPdp: 'Rabatt wird automatisch abgezogen.',
  appliedSublineOtherProduct: 'Der Rabatt gilt für andere Produkte aus der Aktion.',
  minOrderSuffix: (amount: string) => ` Ab ${amount} Bestellwert.`,
  validUntilSuffix: (date: string) => ` Gültig bis ${date}.`,

  // Unavailable / rejected / invalid — each is a {headline, subline} pair.
  unavailableHeadline: (code: string) => `Code ${code} wurde vorgemerkt`,
  unavailableSubline: 'Die Anwendung wird beim Bestellabschluss geprüft.',

  rejectedHeadline: (code: string) => `Code ${code} konnte nicht angewendet werden`,
  rejectedSubline: 'Der Rabattcode passt nicht zum aktuellen Warenkorb.',

  invalidNotfound: 'Dieser Rabattcode ist uns nicht bekannt.',
  invalidExpired: 'Dieser Rabattcode ist leider abgelaufen.',
  invalidInactive: 'Dieser Rabattcode ist aktuell nicht aktiv.',
  invalidScheduled: 'Dieser Rabattcode ist noch nicht aktiv.',

  // Stage-2 offer (ORT-3060): a not-yet-applied, Shopware-maintained code shown with an
  // Anwenden button. Reuses the same discount interpretation as the applied banner.
  offerHeadlinePrefix: 'Sichere dir ',
  offerHeadlineConnector: ' mit Code ',
  offerSubline: 'Rabatt wird beim Bestellabschluss abgezogen.',
  offerApply: 'Anwenden',

  dismiss: 'Hinweis schließen',
} as const;

interface PromoBannerProps {
  state: PromoState;
}

export const PromoBanner = ({ state }: PromoBannerProps) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  if (state.kind === 'idle' || state.kind === 'error') return null;

  if (state.kind === 'loading') {
    return (
      <div className="promo-banner promo-banner--loading" role="status" aria-live="polite">
        <div className="promo-banner__body">
          <span className="promo-banner__headline">
            <span className="promo-banner__loading-dot" aria-hidden="true" />
            {COPY.loading}
          </span>
        </div>
      </div>
    );
  }

  if (state.kind === 'invalid') {
    return (
      <BannerShell variant="invalid" onDismiss={() => setDismissed(true)}>
        <span className="promo-banner__headline">{invalidHeadlineFor(state.metadata.status)}</span>
      </BannerShell>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <BannerShell variant="unavailable" onDismiss={() => setDismissed(true)}>
        <span className="promo-banner__headline">{COPY.unavailableHeadline(state.code)}</span>
        <span className="promo-banner__subline">{COPY.unavailableSubline}</span>
      </BannerShell>
    );
  }

  if (state.kind === 'rejected') {
    return (
      <BannerShell variant="rejected" onDismiss={() => setDismissed(true)}>
        <span className="promo-banner__headline">{COPY.rejectedHeadline(state.code)}</span>
        <span className="promo-banner__subline">{COPY.rejectedSubline}</span>
      </BannerShell>
    );
  }

  // applied — the only state that quotes a concrete discount value.
  const variant: BannerVariant = state.eligibleForThisProduct === false ? 'other' : 'success';
  return (
    <BannerShell variant={variant} onDismiss={() => setDismissed(true)}>
      <AppliedHeadline state={state} />
      <span className="promo-banner__subline">{appliedSublineFor(state)}</span>
    </BannerShell>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Shell + headline composition

type BannerVariant = 'success' | 'other' | 'invalid' | 'rejected' | 'unavailable';

interface BannerShellProps {
  variant: BannerVariant;
  onDismiss: () => void;
  children: preact.ComponentChildren;
}

const BannerShell = ({ variant, onDismiss, children }: BannerShellProps) => (
  <div
    className={`promo-banner promo-banner--${variant}`}
    role="status"
    aria-live="polite"
  >
    <div className="promo-banner__body">{children}</div>
    <button
      type="button"
      className="promo-banner__dismiss"
      aria-label={COPY.dismiss}
      onClick={onDismiss}
    />
  </div>
);

const AppliedHeadline = ({ state }: { state: Extract<PromoState, { kind: 'applied' }> }): JSX.Element => {
  // Shopware-maintained rich text (ORT-3110) replaces the composed headline when present.
  const userDescription = state.metadata.userDescription?.trim();
  if (userDescription) {
    return (
      <span
        className="promo-banner__headline"
        dangerouslySetInnerHTML={{ __html: sanitizePromoHtml(userDescription) }}
      />
    );
  }

  const discount = appliedDiscountFor(state.metadata);
  return (
    <span className="promo-banner__headline">
      {COPY.appliedHeadlinePrefix}
      <strong>{state.code}</strong>
      {COPY.appliedHeadlineConnector}
      <span className="promo-banner__headline-discount"><strong>{discount}</strong></span>
    </span>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Copy selectors (all user-facing strings sourced from COPY)

export function appliedDiscountFor(metadata: PromoMetadata): string {
  const scope = metadata.discountScope;
  const amount = formatDiscountAmount(metadata);

  if (scope === 'delivery') {
    if (metadata.discountType === 'percentage' && metadata.discountValue === 100) {
      return COPY.appliedDiscountShippingFree;
    }
    return amount ? COPY.appliedDiscountShippingPartial(amount) : COPY.appliedDiscountShippingGeneric;
  }

  if (scope === 'cart') {
    if (!amount) return COPY.appliedDiscountCartGeneric;
    return metadata.discountType === 'percentage'
      ? COPY.appliedDiscountCartPercentage(amount)
      : COPY.appliedDiscountCartFixed(amount);
  }

  // set / set_group / pickitem / unknown — Shopware decides at cart-add.
  return COPY.appliedDiscountGeneric;
}

function appliedSublineFor(state: Extract<PromoState, { kind: 'applied' }>): string {
  let base: string;
  if (state.eligibleForThisProduct === false) {
    base = COPY.appliedSublineOtherProduct;
  } else {
    // priceOnPdp promotions already show the discount as a Streichpreis on the PDP (ORT-3110).
    base = state.metadata.priceOnPdp ? COPY.appliedSublinePriceOnPdp : COPY.appliedSublineDefault;
  }
  return base + suffixesFor(state.metadata);
}

export function suffixesFor(metadata: PromoMetadata): string {
  const parts: string[] = [];
  if (metadata.minOrderValue) parts.push(COPY.minOrderSuffix(formatEur(metadata.minOrderValue)));
  if (metadata.validUntil) parts.push(COPY.validUntilSuffix(formatShortDate(metadata.validUntil)));
  return parts.join('');
}

function invalidHeadlineFor(status: PromoMetadata['status']): string {
  switch (status) {
    case 'expired':   return COPY.invalidExpired;
    case 'inactive':  return COPY.invalidInactive;
    case 'scheduled': return COPY.invalidScheduled;
    default:          return COPY.invalidNotfound;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Formatters (locale-bound; no user-facing copy)

function formatDiscountAmount(m: PromoMetadata): string | null {
  if (m.discountValue === null) return null;
  if (m.discountType === 'percentage') return `${formatNumber(m.discountValue)}%`;
  if (m.discountType === 'absolute' || m.discountType === 'fixed' || m.discountType === 'fixed_unit') {
    return formatEur(m.discountValue);
  }
  return null;
}

function formatEur(value: number): string {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
}

function formatShortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
